import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Trash2,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Download,
  Copy,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { toast, Toaster } from "sonner";
import { cn } from "@/lib/utils";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { API } from "@/lib/api";

const SERVICE_TYPES = [
  "Ironing",
  "Wash and Fold",
  "Wash and Iron",
  "Dry Cleaning",
  "Tailoring",
  "Other Services",
  "Commerce"
];

const initialLineItem = { service_type: "", custom_service: "", quantity: 1, cost_price: 0, selling_price: 0 };

const isJSONValid = (str) => { try { JSON.parse(str); return true; } catch { return false; } };

export const ChatwootDashboardApp = () => {
  const [chatwootContext, setChatwootContext] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    phone: "",
    contact_id: "",
    conversation_id: "",
    vendor_id: "",
    invoice_date: new Date(),
    discount: 0,
    delivery_fee: 0,
    rm_name: "",
    payee_name: ""
  });

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [lineItems, setLineItems] = useState([{ ...initialLineItem }]);
  const [rmList, setRmList] = useState([]);
  const [payeeList, setPayeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [createdInvoices, setCreatedInvoices] = useState(null); // { customer, vendor }

  // Duplicate handling
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);

  // Listen for Chatwoot context
  useEffect(() => {
    const handleMessage = (event) => {
      if (!isJSONValid(event.data)) return;
      const eventData = JSON.parse(event.data);
      if (eventData.event === "appContext") {
        setChatwootContext(eventData.data);
        setIsConnected(true);
        const contact = eventData.data?.contact;
        const conversation = eventData.data?.conversation;
        if (contact) {
          setFormData(prev => ({
            ...prev,
            customer_name: contact.name || prev.customer_name,
            phone: contact.phone_number || prev.phone,
            contact_id: String(contact.id) || prev.contact_id,
            conversation_id: conversation?.id ? String(conversation.id) : prev.conversation_id
          }));
        }
      }
    };
    window.addEventListener("message", handleMessage);
    requestChatwootContext();
    fetchVendors();
    fetchStaff();
    return () => window.removeEventListener("message", handleMessage);
  }, []);


  const requestChatwootContext = () => {
    try { window.parent.postMessage('chatwoot-dashboard-app:fetch-info', '*'); }
    catch { console.log("Not in Chatwoot iframe context"); }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API}/vendors`);
      const data = await res.json();
      setVendors(data);
    } catch { toast.error("Failed to load vendors"); }
  };

  const fetchStaff = async () => {
    try {
      const [rmRes, payeeRes] = await Promise.all([
        fetch(`${API}/staff?team=Relations+Manager`),
        fetch(`${API}/staff?team=Payee`),
      ]);
      setRmList(await rmRes.json());
      setPayeeList(await payeeRes.json());
    } catch { console.error("Failed to load staff"); }
  };


  // ── Calculations ──────────────────────────────────────────────────────────

  const calculations = useMemo(() => {
    const vendor_subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.cost_price, 0);
    const customer_subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.selling_price, 0);
    const pokus_fees = customer_subtotal - vendor_subtotal;
    const discount = parseFloat(formData.discount) || 0;
    const delivery_fee = parseFloat(formData.delivery_fee) || 0;
    const vendor_total = vendor_subtotal + delivery_fee;
    const customer_total = customer_subtotal + delivery_fee - discount;
    return { vendor_subtotal, customer_subtotal, pokus_fees, discount, delivery_fee, vendor_total, customer_total, subtotal: customer_subtotal, total_payable: customer_total };
  }, [lineItems, formData.discount, formData.delivery_fee]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

  // ── Form handlers ─────────────────────────────────────────────────────────

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setSelectedVendor(vendor || null);
    handleInputChange("vendor_id", vendorId);
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      if (field === "service_type") updated[index] = { ...updated[index], service_type: value, custom_service: "" };
      else if (field === "custom_service") updated[index] = { ...updated[index], custom_service: value };
      else updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, { ...initialLineItem }]);
  const removeLineItem = (index) => { if (lineItems.length > 1) setLineItems(prev => prev.filter((_, i) => i !== index)); };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateForm = () => {
    if (!formData.customer_name.trim()) { toast.error("Customer name is required"); return false; }
    if (!formData.phone.trim()) { toast.error("Phone number is required"); return false; }
    if (!formData.vendor_id) { toast.error("Please select a vendor"); return false; }
    const valid = lineItems.filter(i => i.service_type && i.quantity > 0 && i.cost_price > 0 && i.selling_price > 0);
    if (valid.length === 0) { toast.error("At least one valid line item with CP and SP is required"); return false; }
    for (const item of lineItems) {
      if ((item.service_type === "Other Services" || item.service_type === "Commerce") && !item.custom_service.trim()) {
        toast.error(`Please specify the ${item.service_type === "Commerce" ? "commerce" : "service"} type`);
        return false;
      }
    }
    return true;
  };

  // ── Payloads ──────────────────────────────────────────────────────────────

  const validLineItems = () => lineItems.filter(i => i.service_type && i.quantity > 0 && i.cost_price > 0 && i.selling_price > 0);

  const customerPayload = () => ({
    customer_name: formData.customer_name,
    phone: formData.phone,
    contact_id: formData.contact_id,
    invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
    line_items: validLineItems().map(item => ({
      service_type: item.service_type,
      custom_service: item.custom_service || "",
      quantity: item.quantity,
      price_per_unit: item.selling_price,
    })),
    discount: formData.discount,
    delivery_fee: formData.delivery_fee,
    payment_link: "https://razorpay.me/@pokus"
  });

  const vendorPayload = () => ({
    vendor_id: formData.vendor_id,
    invoice_date: format(formData.invoice_date, "yyyy-MM-dd"),
    line_items: validLineItems().map(item => ({
      service_type: (item.service_type === "Other Services" || item.service_type === "Commerce")
        ? item.custom_service : item.service_type,
      quantity: item.quantity,
      cost_price: item.cost_price,
      selling_price: item.selling_price,
    })),
    delivery_fee: formData.delivery_fee,
    discount: formData.discount,
    customer_name: formData.customer_name,
    customer_phone: formData.phone,
    contact_id: formData.contact_id,
    rm_name: formData.rm_name,
    payee_name: formData.payee_name
  });

  // ── API calls ─────────────────────────────────────────────────────────────

  const checkForDuplicate = async () => {
    const res = await fetch(`${API}/invoices/check-duplicate`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone: formData.phone, invoice_date: format(formData.invoice_date, "yyyy-MM-dd"), total_amount: calculations.total_payable })
    });
    if (!res.ok) throw new Error("Duplicate check failed");
    return res.json();
  };

  const createCustomerInvoice = async () => {
    const res = await fetch(`${API}/invoices`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(customerPayload()) });
    if (!res.ok) throw new Error("Failed to create customer invoice");
    return res.json();
  };

  const replaceCustomerInvoice = async (existingId) => {
    const res = await fetch(`${API}/invoices/replace`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...customerPayload(), existing_invoice_id: existingId }) });
    if (!res.ok) throw new Error("Failed to replace customer invoice");
    return res.json();
  };

  const createVendorInvoice = async () => {
    const res = await fetch(`${API}/vendor-invoices`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(vendorPayload()) });
    if (!res.ok) throw new Error("Failed to create vendor invoice");
    return res.json();
  };

  // ── PDF generation ────────────────────────────────────────────────────────

  const generateCustomerPDF = async (invoice) => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const L = 12, R = pw - 12;
    let y = 14;
    const ensureSpace = (n = 8) => { if (y + n > ph - 14) { pdf.addPage(); y = 14; } };
    const amt = (v = 0) => `Rs ${Number(v || 0).toFixed(2)}`;

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(16);
    pdf.text("Pokus Technologies Private Limited", L, y); y += 7;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    pdf.text("GSTIN: 29AAQCP4908B1Z8", L, y); y += 5;
    pdf.text("INVOICE", L, y); y += 8;
    pdf.setDrawColor(220, 228, 238); pdf.line(L, y, R, y); y += 7;

    pdf.text(`Bill To: ${invoice.customer_name || "-"}`, L, y); y += 5;
    pdf.text(`Phone: ${invoice.phone || "-"}`, L, y);
    pdf.text(`Invoice: ${invoice.invoice_number || "-"}`, R, y - 5, { align: "right" });
    pdf.text(`Date: ${invoice.invoice_date ? format(new Date(invoice.invoice_date), "PPP") : "-"}`, R, y, { align: "right" });
    y += 8;

    const cSvc = L, cQty = 112, cPrc = 138, cTot = R;
    pdf.setFont("helvetica", "bold");
    pdf.text("Service", cSvc, y); pdf.text("Qty", cQty, y); pdf.text("Price", cPrc, y); pdf.text("Total", cTot, y, { align: "right" });
    y += 3; pdf.setDrawColor(220, 228, 238); pdf.line(L, y, R, y); y += 5;

    pdf.setFont("helvetica", "normal");
    (invoice.line_items || []).forEach(item => {
      ensureSpace(9);
      pdf.text((item.custom_service || item.service_type || "-").toString().slice(0, 42), cSvc, y);
      pdf.text(String(item.quantity ?? 0), cQty, y);
      pdf.text(amt(item.price_per_unit), cPrc, y);
      pdf.text(amt(item.line_total), cTot, y, { align: "right" });
      y += 6;
    });

    y += 2; pdf.line(L, y, R, y); y += 6;
    ensureSpace(26);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Subtotal: ${amt(invoice.subtotal)}`, R, y, { align: "right" }); y += 5;
    if ((invoice.discount || 0) > 0) { pdf.text(`Discount: -${amt(invoice.discount)}`, R, y, { align: "right" }); y += 5; }
    if ((invoice.delivery_fee || 0) > 0) { pdf.text(`Delivery Fee: +${amt(invoice.delivery_fee)}`, R, y, { align: "right" }); y += 5; }
    pdf.setFont("helvetica", "bold");
    pdf.text(`Total Payable: ${amt(invoice.total_payable)}`, R, y, { align: "right" }); y += 10;

    ensureSpace(40);
    const paymentLink = invoice.payment_link || "https://razorpay.me/@pokus";
    const qrImg = await QRCode.toDataURL(paymentLink, { width: 120, margin: 1, color: { dark: "#0047FF", light: "#ffffff" } });
    pdf.setFont("helvetica", "normal"); pdf.text("Scan to Pay", L, y); y += 2;
    pdf.addImage(qrImg, "PNG", L, y, 26, 26);
    pdf.setFontSize(9);
    pdf.setTextColor(0, 71, 255);
    pdf.text(paymentLink, L + 30, y + 14);
    const linkW = pdf.getTextWidth(paymentLink);
    pdf.link(L + 30, y + 14 - 3, linkW, 4, { url: paymentLink });
    pdf.setTextColor(0, 0, 0);

    downloadPDFBlob(pdf, `invoice_${invoice.invoice_number || invoice.id || "download"}.pdf`);
    toast.success("Customer PDF downloaded!");
  };

  const generateVendorPDF = async (invoice) => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const L = 12, R = pw - 12;
    let y = 14;
    const ensureSpace = (n = 8) => { if (y + n > ph - 14) { pdf.addPage(); y = 14; } };
    const amt = (v = 0) => `Rs ${Number(v || 0).toFixed(2)}`;

    // Vendor header
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(16);
    pdf.text((invoice.vendor_name || "Vendor").toUpperCase(), L, y); y += 7;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    pdf.text(`Prop: ${invoice.vendor_name || "-"}`, L, y); y += 5;
    if (invoice.vendor_address) { pdf.text(invoice.vendor_address, L, y); y += 5; }
    pdf.text(`Phone: ${invoice.vendor_contact || "-"}`, L, y); y += 8;
    pdf.setDrawColor(220, 228, 238); pdf.line(L, y, R, y); y += 7;

    pdf.text(`Invoice No: ${invoice.invoice_number || "-"}`, L, y);
    pdf.text(`Date: ${invoice.invoice_date ? format(new Date(invoice.invoice_date), "dd MMM yyyy") : "-"}`, R, y, { align: "right" });
    y += 5;
    pdf.setFont("helvetica", "bold"); pdf.text("Billed To: Pokus AI (Pokus Technologies Pvt Ltd)", L, y);
    pdf.setFont("helvetica", "normal"); y += 5;
    pdf.text("GSTIN: 29AAQCP4908B1Z8", L, y); y += 8;

    // Line items
    const cNum = L, cSvc = L + 8, cQty = 112, cPrc = 138, cTot = R;
    pdf.setFont("helvetica", "bold");
    pdf.text("#", cNum, y); pdf.text("Item / Service", cSvc, y); pdf.text("Qty", cQty, y); pdf.text("Rate", cPrc, y); pdf.text("Amount", cTot, y, { align: "right" });
    y += 3; pdf.setDrawColor(220, 228, 238); pdf.line(L, y, R, y); y += 5;

    pdf.setFont("helvetica", "normal");
    (invoice.line_items || []).forEach((item, i) => {
      ensureSpace(9);
      pdf.text(String(i + 1), cNum, y);
      pdf.text((item.service_type || "-").toString().slice(0, 42), cSvc, y);
      pdf.text(String(item.quantity ?? 0), cQty, y);
      pdf.text(amt(item.price_per_unit), cPrc, y);
      pdf.text(amt(item.line_total), cTot, y, { align: "right" });
      y += 6;
    });

    y += 2; pdf.line(L, y, R, y); y += 6;
    ensureSpace(26);
    pdf.text(`Subtotal: ${amt(invoice.subtotal)}`, R, y, { align: "right" }); y += 5;
    if ((invoice.delivery_fee || 0) > 0) { pdf.text(`Delivery Fee: +${amt(invoice.delivery_fee)}`, R, y, { align: "right" }); y += 5; }
    pdf.setFont("helvetica", "bold");
    pdf.text(`Total Payable: ${amt(invoice.total_payable)}`, R, y, { align: "right" }); y += 10;

    // Signature
    if (invoice.signature_data) {
      ensureSpace(30);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
      pdf.text("Authorised Signatory", R, y, { align: "right" }); y += 2;
      try { pdf.addImage(invoice.signature_data, "PNG", R - 40, y, 40, 14); } catch {}
      y += 16;
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(10);
      pdf.text(invoice.vendor_name || "", R, y, { align: "right" }); y += 8;
    }

    // UPI QR
    if (invoice.vendor_upi_id) {
      ensureSpace(40);
      const upiQR = await QRCode.toDataURL(
        `upi://pay?pa=${invoice.vendor_upi_id}&pn=${encodeURIComponent(invoice.vendor_name || "")}`,
        { width: 120, margin: 1, color: { dark: "#0f172a", light: "#ffffff" } }
      );
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
      pdf.text("Scan to Pay via UPI", L, y); y += 2;
      pdf.addImage(upiQR, "PNG", L, y, 26, 26);
      const upiLabel = `UPI: ${invoice.vendor_upi_id}`;
      const upiUrl = `upi://pay?pa=${invoice.vendor_upi_id}&pn=${encodeURIComponent(invoice.vendor_name || "")}`;
      pdf.setTextColor(0, 71, 255);
      pdf.text(upiLabel, L + 30, y + 14);
      const upiLinkW = pdf.getTextWidth(upiLabel);
      pdf.link(L + 30, y + 14 - 3, upiLinkW, 4, { url: upiUrl });
      pdf.setTextColor(0, 0, 0);
    }

    downloadPDFBlob(pdf, `vendor_invoice_${invoice.invoice_number || invoice.id || "download"}.pdf`);
    toast.success("Vendor PDF downloaded!");
  };

  const downloadPDFBlob = (pdf, fileName) => {
    const blob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl; link.download = fileName; link.rel = "noopener";
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    if (window.self !== window.top) {
      setTimeout(() => { if (!document.hidden) window.open(blobUrl, "_blank", "noopener,noreferrer"); }, 300);
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  };

  // ── Submit flow ───────────────────────────────────────────────────────────

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const dupCheck = await checkForDuplicate();
      if (dupCheck.is_duplicate) {
        setExistingInvoice(dupCheck.existing_invoice);
        setDuplicateDialogOpen(true);
        setLoading(false);
        return;
      }
      await submitBothInvoices();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create invoices");
      setLoading(false);
    }
  };

  const submitBothInvoices = async (existingCustomerInvoiceId = null) => {
    const [customerInvoice, vendorInvoice] = await Promise.all([
      existingCustomerInvoiceId ? replaceCustomerInvoice(existingCustomerInvoiceId) : createCustomerInvoice(),
      createVendorInvoice()
    ]);
    setCreatedInvoices({ customer: customerInvoice, vendor: vendorInvoice });
    toast.success(`Invoices created: ${customerInvoice.invoice_number} & ${vendorInvoice.invoice_number}`);
    await generateCustomerPDF(customerInvoice);
    await generateVendorPDF(vendorInvoice);
    setLoading(false);
  };

  const handleReplace = async () => {
    setDuplicateDialogOpen(false);
    setLoading(true);
    try { await submitBothInvoices(existingInvoice.id); }
    catch (err) { console.error(err); toast.error("Failed to create invoices"); setLoading(false); }
  };

  const resetForm = () => {
    setLineItems([{ ...initialLineItem }]);
    setFormData(prev => ({ ...prev, vendor_id: "", discount: 0, delivery_fee: 0, rm_name: "", payee_name: "" }));
    setSelectedVendor(null);
    setCreatedInvoices(null);
    setExistingInvoice(null);
    setCopied(false);
    requestChatwootContext();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white p-4" data-testid="chatwoot-dashboard-app">
      <Toaster position="top-center" richColors />

      {/* Duplicate Invoice Dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Duplicate Invoice Found
            </AlertDialogTitle>
            <AlertDialogDescription>
              An invoice already exists for this customer on {format(formData.invoice_date, "PPP")}.
              {existingInvoice && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                  <p><strong>Invoice:</strong> {existingInvoice.invoice_number}</p>
                  <p><strong>Amount:</strong> {formatCurrency(existingInvoice.total_payable)}</p>
                </div>
              )}
              <p className="mt-3">Do you want to replace the customer invoice?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReplace} className="bg-amber-500 hover:bg-amber-600">
              Replace & Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Connection Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {isConnected
            ? <CheckCircle className="w-4 h-4 text-green-500" />
            : <AlertCircle className="w-4 h-4 text-amber-500" />}
          <span className="text-sm text-slate-600">{isConnected ? "Connected to Chatwoot" : "Standalone Mode"}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={requestChatwootContext} className="text-slate-500">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Success State */}
      {createdInvoices ? (
        <div className="space-y-3">
          <div className="text-center py-2">
            <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <h2 className="text-lg font-bold text-slate-900">Invoice Saved!</h2>
            <p className="text-xs text-slate-500 font-mono">{createdInvoices.vendor.invoice_number} · {formData.customer_name}</p>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Customer invoice downloaded</p>
                  <p className="text-xs text-slate-500">PDF saved to your device</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900">Vendor invoice — Merchant Signature Pending</p>
                  <p className="text-xs text-slate-500">Share the link below with {createdInvoices.vendor.vendor_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Merchant Signing Link</p>
              <div className="bg-slate-50 rounded p-2 font-mono text-xs text-slate-600 break-all border border-slate-200">
                {`${window.location.origin}/sign/${createdInvoices.vendor.signing_token}`}
              </div>
              <Button
                size="sm"
                className={`w-full ${copied ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                variant={copied ? "default" : "outline"}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/sign/${createdInvoices.vendor.signing_token}`);
                  setCopied(true);
                  toast.success("Link copied!");
                  setTimeout(() => setCopied(false), 2500);
                }}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                {copied ? "Copied!" : "Copy Signing Link"}
              </Button>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => generateCustomerPDF(createdInvoices.customer)}>
              <Download className="w-3.5 h-3.5 mr-1" /> Customer PDF
            </Button>
            <Button size="sm" className="flex-1 bg-[#0047FF] hover:bg-[#0033CC]" onClick={resetForm}>
              New Invoice
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          {/* Customer Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">Customer</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Name *</Label>
                  <Input value={formData.customer_name} onChange={(e) => handleInputChange("customer_name", e.target.value)} placeholder="Customer name" className="h-9 text-sm" data-testid="cw-input-customer-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone *</Label>
                  <Input value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="Phone" className="h-9 text-sm" data-testid="cw-input-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Contact ID</Label>
                  <Input value={formData.contact_id} onChange={(e) => handleInputChange("contact_id", e.target.value)} placeholder="Auto-filled" className="h-9 text-sm bg-slate-50" data-testid="cw-input-contact-id" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full h-9 justify-start text-left text-sm font-normal" data-testid="cw-input-date">
                        {format(formData.invoice_date, "MMM dd, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={formData.invoice_date} onSelect={(date) => { handleInputChange("invoice_date", date || new Date()); setDatePickerOpen(false); }} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vendor */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">Vendor *</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                <SelectTrigger className="h-9 text-sm" data-testid="cw-select-vendor">
                  <SelectValue placeholder="Select a vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.merchant_name} — {v.service_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedVendor && (
                <div className="text-xs text-slate-500 space-y-0.5 pt-1">
                  <p>Contact: {selectedVendor.contact_no}</p>
                  {selectedVendor.upi_id && <p>UPI: {selectedVendor.upi_id}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Services</CardTitle>
              <Button type="button" variant="ghost" size="sm" onClick={addLineItem} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-12 gap-1 text-[10px] font-semibold text-slate-400 uppercase px-0.5">
                <div className="col-span-3">Service</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-2">CP</div>
                <div className="col-span-2">SP</div>
                <div className="col-span-2">Fee</div>
                <div className="col-span-1">Amt</div>
                <div className="col-span-1"></div>
              </div>
              {lineItems.map((item, index) => {
                const vendorAmt = item.quantity * item.cost_price;
                const customerAmt = item.quantity * item.selling_price;
                const pokusFee = customerAmt - vendorAmt;
                const needsCustomService = item.service_type === "Other Services" || item.service_type === "Commerce";
                return (
                  <div key={index} className="space-y-2">
                    <div className="grid grid-cols-12 gap-1 items-center">
                      <div className="col-span-3">
                        <Select value={item.service_type} onValueChange={(value) => handleLineItemChange(index, "service_type", value)}>
                          <SelectTrigger className="h-9 text-sm" data-testid={`cw-service-type-${index}`}>
                            <SelectValue placeholder="Service" />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICE_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)} className="h-9 text-sm font-mono" placeholder="Qty" data-testid={`cw-quantity-${index}`} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" min="0" step="0.01" value={item.cost_price} onChange={(e) => handleLineItemChange(index, "cost_price", e.target.value)} className="h-9 text-sm font-mono" placeholder="CP" data-testid={`cw-cp-${index}`} />
                      </div>
                      <div className="col-span-2">
                        <Input type="number" min="0" step="0.01" value={item.selling_price} onChange={(e) => handleLineItemChange(index, "selling_price", e.target.value)} className="h-9 text-sm font-mono" placeholder="SP" data-testid={`cw-sp-${index}`} />
                      </div>
                      <div className={`col-span-2 text-right text-xs font-mono ${pokusFee >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(pokusFee)}</div>
                      <div className="col-span-1 text-right text-xs font-mono text-slate-700">{formatCurrency(customerAmt)}</div>
                      <div className="col-span-1 flex justify-center">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)} disabled={lineItems.length === 1} className="h-8 w-8 text-slate-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {needsCustomService && (
                      <Input value={item.custom_service} onChange={(e) => handleLineItemChange(index, "custom_service", e.target.value)} placeholder={`Specify ${item.service_type === "Commerce" ? "commerce" : "service"} type`} className="h-8 text-sm" data-testid={`cw-custom-service-${index}`} />
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Totals */}
          <Card className="border-slate-200">
            <CardContent className="px-4 py-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Discount (₹)</Label>
                  <Input type="number" min="0" value={formData.discount} onChange={(e) => handleInputChange("discount", parseFloat(e.target.value) || 0)} className="h-9 text-sm font-mono" data-testid="cw-discount" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Delivery Fee (₹)</Label>
                  <Input type="number" min="0" value={formData.delivery_fee} onChange={(e) => handleInputChange("delivery_fee", parseFloat(e.target.value) || 0)} className="h-9 text-sm font-mono" data-testid="cw-delivery-fee" />
                </div>
              </div>
              <div className="pt-2 border-t border-slate-100 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Vendor Subtotal (CP)</span><span className="font-mono">{formatCurrency(calculations.vendor_subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Customer Subtotal (SP)</span><span className="font-mono">{formatCurrency(calculations.customer_subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-emerald-600">Pokus Fees</span><span className="font-mono text-emerald-600">{formatCurrency(calculations.pokus_fees)}</span></div>
                {calculations.discount > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Discount (customer only)</span><span className="font-mono text-red-500">-{formatCurrency(calculations.discount)}</span></div>}
                {calculations.delivery_fee > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Delivery</span><span className="font-mono">+{formatCurrency(calculations.delivery_fee)}</span></div>}
                <div className="flex justify-between text-sm font-bold pt-1 border-t border-slate-200 mt-1">
                  <span>Vendor Total</span>
                  <span className="font-mono">{formatCurrency(calculations.vendor_total)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-[#0047FF]">
                  <span>Customer Total</span>
                  <span className="font-mono">{formatCurrency(calculations.total_payable)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* RM & Payee */}
          <Card className="border-slate-200">
            <CardContent className="px-4 py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">RM</Label>
                  <Select value={formData.rm_name} onValueChange={(v) => handleInputChange("rm_name", v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="cw-input-rm-name"><SelectValue placeholder="Select RM" /></SelectTrigger>
                    <SelectContent>
                      {rmList.map(s => <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Payee</Label>
                  <Select value={formData.payee_name} onValueChange={(v) => handleInputChange("payee_name", v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="cw-input-payee-name"><SelectValue placeholder="Select Payee" /></SelectTrigger>
                    <SelectContent>
                      {payeeList.map(s => <SelectItem key={s.id} value={s.full_name}>{s.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#0047FF] hover:bg-[#0033CC] text-base" data-testid="cw-save-btn">
            {loading ? "Saving..." : (
              <><Save className="w-5 h-5 mr-2" /> Save & Download</>
            )}
          </Button>
        </form>
      )}
    </div>
  );
};
